import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Search, Add } from "@mui/icons-material";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import KanbanColumn from "./KanbanColumn";
import TaskCard from "./TaskCard";
import CreateTaskDialog from "./CreateTaskDialog";
import { useTasks, useMoveTask } from "../hooks/useTasks";
import useTaskStore from "../store/taskStore";

const columns = [
  { id: "backlog", title: "Backlog", color: "#9c27b0" },
  { id: "in-progress", title: "In Progress", color: "#2196f3" },
  { id: "review", title: "Review", color: "#ff9800" },
  { id: "done", title: "Done", color: "#4caf50" },
];

const KanbanBoard = () => {
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { isLoading, error } = useTasks();
  const moveTask = useMoveTask();
  const { tasks, searchQuery, setSearchQuery } = useTaskStore();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const filteredTasks = useMemo(() => {
    if (!searchQuery) return tasks;

    const query = searchQuery.toLowerCase();
    return tasks.filter(
      (task) =>
        task.title.toLowerCase().includes(query) ||
        task.description.toLowerCase().includes(query)
    );
  }, [tasks, searchQuery]);

  const tasksByColumn = useMemo(() => {
    const grouped = {
      backlog: [],
      "in-progress": [],
      review: [],
      done: [],
    };

    filteredTasks.forEach((task) => {
      if (grouped[task.column]) {
        grouped[task.column].push(task);
      }
    });

    return grouped;
  }, [filteredTasks]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    setOverId(over?.id || null);

    if (!over) return;

    const activeTask = tasks.find((t) => t.id === active.id);
    if (!activeTask) return;

    // Check if we're over a column
    const overColumn = columns.find((col) => col.id === over.id);
    if (overColumn && activeTask.column !== overColumn.id) {
      // Move to new column
      moveTask.mutate({
        id: activeTask.id,
        column: overColumn.id,
      });
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      setOverId(null);
      return;
    }

    const activeTask = tasks.find((t) => t.id === active.id);
    const overTask = tasks.find((t) => t.id === over.id);

    if (activeTask && overTask && activeTask.column === overTask.column) {
      // Same column - no action needed for now
      setActiveId(null);
      setOverId(null);
      return;
    } else if (activeTask) {
      // Check if we're over a column (not a task)
      let targetColumn = columns.find((col) => col.id === over.id);

      // Check if we're dropping over a dropzone
      if (!targetColumn && over.id.startsWith("dropzone-")) {
        const columnId = over.id.replace("dropzone-", "");
        targetColumn = columns.find((col) => col.id === columnId);
      }

      if (targetColumn && activeTask.column !== targetColumn.id) {
        moveTask.mutate({
          id: activeTask.id,
          column: targetColumn.id,
        });
      }

      // Check if we're dropping over a task in a different column
      if (overTask && activeTask.column !== overTask.column) {
        moveTask.mutate({
          id: activeTask.id,
          column: overTask.column,
        });
      }
    }

    setActiveId(null);
    setOverId(null);
  };

  const activeTask = useMemo(
    () => tasks.find((task) => task.id === activeId),
    [activeId, tasks]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load tasks. Please make sure json-server is running on port
          4000.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Kanban Board
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <TextField
            placeholder="Search tasks..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            Add Task
          </Button>
        </Box>
      </Paper>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <Grid container spacing={2} sx={{ minWidth: "100%" }}>
          {columns.map((column) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={column.id}
              sx={{
                minWidth: { xs: "100%", sm: "300px", md: "280px" },
                maxWidth: { xs: "100%", sm: "50%", md: "25%" },
                flex: { xs: "1 1 100%", sm: "1 1 50%", md: "1 1 20%" },
              }}
            >
              <KanbanColumn
                column={column.id}
                title={column.title}
                color={column.color}
                tasks={tasksByColumn[column.id] || []}
                dragOverlay={{ activeId, overId }}
              />
            </Grid>
          ))}
        </Grid>

        <DragOverlay>
          {activeTask ? (
            <Box
              sx={{
                transform: "rotate(5deg) scale(1.1)",
                opacity: 0.95,
                filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.3))",
                transition: "all 0.2s ease",
                zIndex: 9999,
                "& .MuiCard-root": {
                  border: "3px solid #2196f3",
                  backgroundColor: "#fff",
                  boxShadow: "0 20px 40px rgba(33, 150, 243, 0.4)",
                },
              }}
            >
              <TaskCard task={activeTask} />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      <CreateTaskDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </Box>
  );
};

export default KanbanBoard;
